// TarjetaProgramaSection.js — Sección `tarjeta_programa`: la ficha de un programa.
//
// Tarjeta oscura con una fila de etiquetas, título, lista de lo que incluye y
// botones (ir al aula, solicitar acceso, entrar…), con una nota debajo. Con
// `sin_tarjeta` sirve también como bloque de botón + nota suelto sobre el fondo.
//
// Cada botón: { texto, accion: 'url' | 'solicitud' | 'login' | 'registro',
// url, nueva_pestana, estilo: 'primario' | 'secundario' }.
import React from 'react';
import styled, { css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { crearActivador } from './accionesBoton';

const Seccion = styled.section`
  background: ${p => p.$fondo};
  padding: ${p => p.$padding};
  font-family: Arial, Helvetica, sans-serif;
  @media (max-width: 767px) { padding: ${p => p.$paddingMovil}; }
`;

const Tarjeta = styled.div`
  width: 90%;
  max-width: ${p => p.$ancho};
  margin: 0 auto;
  box-sizing: border-box;
  ${p => p.$tarjeta && css`
    border-radius: 23px;
    padding: ${p.$padding};
    background-image: ${p.$fondoTarjeta};
  `}
  h2 {
    margin: 0 0 18px;
    padding: 0;
    font-family: 'Roboto', Helvetica, Arial, sans-serif;
    font-size: ${p => p.$sizeTitulo};
    font-weight: 700;
    line-height: 1.2;
    color: ${p => p.$colorTitulo};
  }
  .lista { color: ${p => p.$colorTexto}; font-size: 16px; line-height: 1.9; margin: 0; }
  @media (max-width: 767px) {
    ${p => p.$tarjeta && 'padding: 26px 20px 30px;'}
    h2 { font-size: 20px; }
  }
`;

const Etiquetas = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px 10px;
  margin: 0 0 26px;
  padding: 9px 16px;
  border: 1px solid ${p => p.$borde};
  border-radius: 999px;
  color: ${p => p.$color};
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  span:not(:last-child)::after { content: ' ·'; }
`;

const Botones = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 28px;
  justify-content: ${p => p.$align};
`;

const baseBoton = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 26px;
  border: none;
  border-radius: 11px;
  font-family: inherit;
  font-weight: 700;
  font-size: 16px;
  letter-spacing: .3px;
  cursor: pointer;
  text-decoration: none;
  transition: all .3s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 22px rgb(168 85 247 / .35); }
`;

const BotonPrimario = styled.button`
  ${baseBoton}
  background: ${p => p.$fondo || 'linear-gradient(90deg, #c026d3 30%, #9333ea 70%, #7e22ce 100%)'};
  color: #fff;
`;

const BotonSecundario = styled.button`
  ${baseBoton}
  background: ${p => p.$fondo || '#fff'};
  color: ${p => p.$color || '#7b2fbe'};
`;

const Nota = styled.p`
  margin: ${p => p.$margen};
  text-align: center;
  color: ${p => p.$color};
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-line;
`;

const TarjetaProgramaSection = ({ config = {}, openLogin, openRegister, tl = (v, d = '') => v || d }) => {
  const c = config;
  const navigate = useNavigate();
  const botones = Array.isArray(c.botones) ? c.botones.filter(b => b && b.texto) : [];
  const etiquetas = Array.isArray(c.etiquetas) ? c.etiquetas.filter(Boolean) : [];
  const conTarjeta = c.sin_tarjeta !== true;

  const activar = crearActivador({ navigate, openLogin, openRegister });

  const nota = tl(c.nota, '');

  return (
    <Seccion
      $fondo={c.color_fondo || '#f8f5ff'}
      $padding={c.padding || '60px 0'}
      $paddingMovil={c.padding_movil || '40px 0'}
    >
      <Tarjeta
        $tarjeta={conTarjeta}
        $ancho={c.ancho_maximo || '700px'}
        $padding={c.padding_tarjeta || '36px 40px 44px'}
        $fondoTarjeta={c.fondo_tarjeta || 'linear-gradient(180deg, #1a0a2e 0%, #2d1054 99%)'}
        $sizeTitulo={c.tamano_titulo || '22px'}
        $colorTitulo={c.color_titulo || '#ffffff'}
        $colorTexto={c.color_texto || '#cbc3d8'}
      >
        {etiquetas.length > 0 && (
          <Etiquetas $borde={c.color_borde_etiquetas || 'rgba(255,255,255,.28)'} $color={c.color_etiquetas || '#e3abfc'}>
            {etiquetas.map((e, i) => <span key={i}>{tl(e, '')}</span>)}
          </Etiquetas>
        )}
        {tl(c.titulo, '') && <h2>{tl(c.titulo, '')}</h2>}
        {tl(c.lista, '') && <div className="lista" dangerouslySetInnerHTML={{ __html: tl(c.lista, '') }} />}
        {botones.length > 0 && (
          <Botones $align={c.alineacion_botones || (conTarjeta ? 'flex-start' : 'center')}>
            {botones.map((b, i) => {
              const Boton = b.estilo === 'secundario' ? BotonSecundario : BotonPrimario;
              return (
                <Boton key={i} type="button" onClick={() => activar(b)} $fondo={b.color_fondo} $color={b.color_texto}>
                  {tl(b.texto, '')}
                </Boton>
              );
            })}
          </Botones>
        )}
      </Tarjeta>
      {nota && (
        <Nota $color={c.color_nota || '#4d4d4d'} $margen={c.margen_nota || (conTarjeta ? '34px 0 0' : '20px 0 0')}>
          {nota}
        </Nota>
      )}
    </Seccion>
  );
};

export default TarjetaProgramaSection;
